// Criação de Configuração de pagamento. Sem edição por design: a configuração
// é imutável porque assinaturas e seguros apontam para ela.
import { ArrowLeftIcon, StoreIcon } from "lucide-react"
import { useTranslation } from "react-i18next"
import {
  Link,
  Navigate,
  useNavigate,
} from "react-router-dom"

import { EmptyState } from "@/components/commons/empty-state"
import { AppBreadcrumb } from "@/components/layout/app-breadcrumb"
import { DashboardContainer } from "@/components/layout/dashboard-container"
import { Header } from "@/components/layout/header"
import { Button } from "@/components/ui/button"
import { FRONTEND_ROUTES } from "@/constants"
import { useAuth } from "@/contexts/auth"
import {
  CreatePaymentConfigurationForm,
  paymentConfigurationDetailHref,
  PaymentConfigurationFeedback,
  useCanWritePaymentConfigurations,
} from "@/features/payment-configurations"
import { useWhiteLabels } from "@/hooks/use-white-labels"
import { useCreatePaymentConfigurationMutation } from "@/services/payment-configurations"
import type { PaymentConfigurationCreatePayload } from "@/types/payment-configurations"

const prefix = "pages.paymentConfigurations"

export function CreatePaymentConfigurationPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // A loja escolhida no menu já vem selecionada; com "Todas as lojas" o
  // operador escolhe no formulário.
  const { selectedWhiteLabel } = useAuth()
  const canWrite = useCanWritePaymentConfigurations()
  const {
    data: whiteLabels = [],
    isLoading,
    isError,
    refetch,
  } = useWhiteLabels()
  const createMutation =
    useCreatePaymentConfigurationMutation()

  // Defesa em profundidade: o gate de rota já barra, e o gateway valida o escopo.
  if (!canWrite) {
    return (
      <Navigate
        replace
        to={FRONTEND_ROUTES.PAYMENT_CONFIGURATIONS.href}
      />
    )
  }

  const handleSubmit = (
    payload: PaymentConfigurationCreatePayload
  ) =>
    createMutation.mutate(payload, {
      onSuccess: ({ configuration }) =>
        navigate(
          paymentConfigurationDetailHref(configuration.id)
        ),
    })

  return (
    <DashboardContainer>
      <Header>
        <AppBreadcrumb
          links={[
            {
              href: FRONTEND_ROUTES.DASHBOARD.href,
              label: t("sidebar.dashboard"),
            },
            { label: t("sidebar.administration") },
            {
              href: FRONTEND_ROUTES.PAYMENT_CONFIGURATIONS
                .href,
              label: t("sidebar.paymentConfigurations"),
            },
            { label: t(`${prefix}.create.breadcrumb`) },
          ]}
        />
      </Header>
      <section className="flex flex-1 flex-col gap-6 overflow-x-hidden p-4 @container">
        <div className="flex flex-col gap-1">
          <h1 className="page-title">
            {t(`${prefix}.create.title`)}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(`${prefix}.create.subtitle`)}
          </p>
        </div>
        {/* Sem white label não há o que escolher no formulário: falha tem
            retry; lista vazia explica e devolve para a listagem. */}
        {isError ? (
          <PaymentConfigurationFeedback
            variant="error"
            onRetry={() => void refetch()}
          />
        ) : !isLoading && whiteLabels.length === 0 ? (
          <EmptyState
            icon={StoreIcon}
            title={t(`${prefix}.create.noWhiteLabelsTitle`)}
            description={t(
              `${prefix}.create.noWhiteLabelsDescription`
            )}
            action={
              <Button asChild size="sm" variant="outline">
                <Link
                  to={
                    FRONTEND_ROUTES.PAYMENT_CONFIGURATIONS
                      .href
                  }
                >
                  <ArrowLeftIcon
                    className="mr-1.5 h-3.5 w-3.5"
                    aria-hidden="true"
                  />
                  {t(`${prefix}.detail.back`)}
                </Link>
              </Button>
            }
          />
        ) : (
          <CreatePaymentConfigurationForm
            whiteLabels={whiteLabels}
            isLoadingWhiteLabels={isLoading}
            initialWhiteLabelId={selectedWhiteLabel?.uuid}
            isSubmitting={createMutation.isPending}
            onSubmit={handleSubmit}
            onCancel={() =>
              navigate(
                FRONTEND_ROUTES.PAYMENT_CONFIGURATIONS.href
              )
            }
          />
        )}
      </section>
    </DashboardContainer>
  )
}
